import {useEffect, useRef, useState, type Dispatch, type SetStateAction} from "react";
import {completeGridSave, type WorkspaceState} from "./workspace";
import {GRID_MAX_BYTES} from "./limits";
import {loadGridProject, saveGridProject, ProjectRequestError, type ProjectHead, type ProjectSaveAttempt} from "./projects";

export function useGridProject(state: WorkspaceState, setState: Dispatch<SetStateAction<WorkspaceState>>, initial: ProjectHead | undefined,
  onCreated: (project: ProjectHead) => void) {
  const [project, setProject] = useState<ProjectHead | undefined>(() => initial ? {
    projectId: initial.projectId, revision: initial.revision, digest: initial.digest, createdAt: initial.createdAt, editPath: initial.editPath,
  } : undefined);
  const knownProject = useRef(project);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [trashed, setTrashed] = useState(false);
  const [retryPending, setRetryPending] = useState(false);
  const attempt = useRef<ProjectSaveAttempt | null>(null);
  const inFlight = useRef(false), mounted = useRef(true);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  const save = async (asNew = false) => {
    if (inFlight.current || (conflict && !asNew) || (asNew && retryPending)) return;
    if (!attempt.current) {
      if (new TextEncoder().encode(JSON.stringify(state.document)).byteLength > GRID_MAX_BYTES) {
        setError("Grid document exceeds 4 MiB. Remove some banners or large SVG assets."); return;
      }
      const head = knownProject.current;
      attempt.current = {document: state.document, key: crypto.randomUUID(), target: !asNew && head ? {projectId: head.projectId, revision: head.revision} : null};
    }
    const submitted = attempt.current;
    inFlight.current = true; setSaving(true); setError(""); setConflict(false); setTrashed(false);
    try {
      const receipt = await saveGridProject(submitted);
      if (!mounted.current) return;
      const head: ProjectHead = {projectId: receipt.projectId, revision: receipt.revision, digest: receipt.digest, createdAt: receipt.createdAt, editPath: receipt.editPath};
      const created = knownProject.current?.projectId !== head.projectId;
      knownProject.current = head; setProject(head);
      if (created) onCreated(head);
      let document = receipt.document;
      if (receipt.headRequired) {
        const currentHead = await loadGridProject(head.projectId);
        if (!mounted.current) return;
        if (currentHead.revision !== head.revision || currentHead.digest !== head.digest) {
          attempt.current = null; setRetryPending(false); setConflict(true);
          setError("Your save was received, but this project now has a newer saved version. Your draft is still here."); return;
        }
        document = currentHead.document;
      }
      if (!document) throw new Error("Save acknowledgement is missing its document");
      setState((current) => completeGridSave(current, submitted.document, document));
      attempt.current = null; setRetryPending(false);
      return {head, document};
    } catch (reason) {
      if (!mounted.current) return;
      const definitive = reason instanceof ProjectRequestError && reason.status < 500;
      if (definitive) attempt.current = null;
      setRetryPending(!definitive);
      setConflict(reason instanceof ProjectRequestError && ["REVISION_CONFLICT", "PROJECT_TRASHED", "PROJECT_NOT_FOUND"].includes(reason.code));
      setTrashed(reason instanceof ProjectRequestError && ["PROJECT_TRASHED", "PROJECT_NOT_FOUND"].includes(reason.code));
      setError(reason instanceof Error ? reason.message : "Could not save your project");
    } finally {inFlight.current = false; if (mounted.current) setSaving(false);}
  };
  return {project, saving, error, conflict, trashed, retryPending, save};
}
